import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, ArrowLeft, RefreshCw, Send, Check, Lock, Gift, Clock } from "lucide-react";
import { QUIZZES, ROUTER_OPTIONS, evaluateQuiz, buildWhatsAppLink } from "../quizData";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STEPS = { router: "router", quiz: "quiz", result: "result" };

const RouterScreen = ({ onPick }) => (
  <section className="max-w-3xl mx-auto px-6 lg:px-8 py-16">
    <div className="text-center mb-12">
      <span className="inline-flex rounded-full bg-green-50 text-green-700 border border-green-200 px-4 py-1.5 text-xs font-bold tracking-widest uppercase">
        Quiz · 60 segundos
      </span>
      <h1 className="mt-6 font-display text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">
        ¿Qué describe <span className="text-green-600">mejor tu situación</span>?
      </h1>
      <p className="mt-4 text-base text-slate-600 max-w-xl mx-auto leading-relaxed">
        Elige una opción y te llevaremos al quiz personalizado para ti. Al terminar, te recomendaremos al terapeuta indicado.
      </p>
    </div>
    <div className="space-y-3" data-testid="quiz-router">
      {ROUTER_OPTIONS.map((opt) => (
        <button
          key={opt.key}
          onClick={() => onPick(opt.key)}
          className="w-full group flex items-center gap-5 rounded-2xl border-2 border-slate-200 bg-white p-5 text-left transition-all hover:border-green-500 hover:shadow-lg hover:-translate-y-0.5"
          data-testid={`quiz-router-${opt.key}`}
        >
          <span className="text-3xl" aria-hidden="true">{opt.icon}</span>
          <span className="flex-1">
            <span className="block font-display font-semibold text-slate-900 text-base">{opt.title}</span>
            <span className="block text-sm text-slate-500 mt-0.5">{opt.subtitle}</span>
          </span>
          <ArrowRight className="h-5 w-5 text-slate-300 group-hover:text-green-600 group-hover:translate-x-1 transition-all" />
        </button>
      ))}
    </div>
  </section>
);

const QuizScreen = ({ quiz, onComplete, onBack }) => {
  const [answers, setAnswers] = useState(Array(quiz.questions.length).fill(null));
  const [idx, setIdx] = useState(0);

  const setAnswer = (val) => {
    const next = [...answers];
    next[idx] = val;
    setAnswers(next);
    setTimeout(() => {
      if (idx + 1 < quiz.questions.length) setIdx(idx + 1);
      else onComplete(next);
    }, 250);
  };

  const progress = Math.round(((idx + 1) / quiz.questions.length) * 100);

  return (
    <section className="max-w-2xl mx-auto px-6 lg:px-8 py-14" data-testid="quiz-questions">
      <div className="flex items-center justify-between mb-5">
        <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900" data-testid="quiz-back-btn">
          <ArrowLeft className="h-4 w-4" /> Cambiar de quiz
        </button>
        <span className="text-xs font-bold tracking-widest uppercase text-green-700">
          {quiz.badge}
        </span>
      </div>

      <div className="w-full h-1.5 rounded-full bg-slate-100 overflow-hidden mb-8">
        <div
          className="h-full bg-gradient-to-r from-green-500 to-emerald-600 transition-all duration-300"
          style={{ width: `${progress}%` }}
        />
      </div>

      <p className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-3">
        Pregunta {idx + 1} de {quiz.questions.length}
      </p>
      <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 leading-tight">
        {quiz.questions[idx]}
      </h2>

      <div className="mt-10 grid grid-cols-2 gap-4">
        <button
          onClick={() => setAnswer(true)}
          className="rounded-2xl border-2 border-slate-200 bg-white py-6 font-display font-bold text-lg text-slate-900 hover:border-green-500 hover:bg-green-50 transition-all"
          data-testid="quiz-answer-yes"
        >
          Sí
        </button>
        <button
          onClick={() => setAnswer(false)}
          className="rounded-2xl border-2 border-slate-200 bg-white py-6 font-display font-bold text-lg text-slate-900 hover:border-rose-400 hover:bg-rose-50 transition-all"
          data-testid="quiz-answer-no"
        >
          No
        </button>
      </div>
    </section>
  );
};

const BOOKING_ANCHOR_BY_SERVICE = {
  terapia_fisica: "/#booking-fisio",
  rehabilitacion: "/#booking-fisio",
  quiropractica: "/#booking-fisio",
  consulta_general: "/#booking-fisio",
  terapias_alternativas: "/#booking-alt",
  estetica: "/#booking-estetica",
};

const COUPON_DURATION_SECONDS = 3 * 60; // 3 minutos
const generateCouponCode = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let code = "QZ25-";
  for (let i = 0; i < 4; i++) code += chars[Math.floor(Math.random() * chars.length)];
  return code;
};

const useCountdown = (totalSeconds) => {
  const [expiresAt] = useState(() => Date.now() + totalSeconds * 1000);
  const [remaining, setRemaining] = useState(totalSeconds);
  useEffect(() => {
    const id = setInterval(() => {
      const r = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
      setRemaining(r);
      if (r === 0) clearInterval(id);
    }, 500);
    return () => clearInterval(id);
  }, [expiresAt]);
  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");
  return { remaining, mm, ss, expired: remaining === 0 };
};

const COLOR_STYLES = {
  green: { bg: "bg-green-50", border: "border-green-200", text: "text-green-700", btn: "bg-green-600 hover:bg-green-700" },
  yellow: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800", btn: "bg-amber-600 hover:bg-amber-700" },
  red: { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-700", btn: "bg-rose-600 hover:bg-rose-700" },
};

const ResultScreen = ({ quiz, result, answers, onRestart }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState("");
  const palette = COLOR_STYLES[result.color];
  const coupon = useMemo(() => generateCouponCode(), []);
  const countdown = useCountdown(COUPON_DURATION_SECONDS);
  const couponActive = !countdown.expired;

  // Compose WhatsApp link including coupon while it's active
  const waLink = useMemo(() => {
    const base = buildWhatsAppLink(quiz, result);
    if (!couponActive) return base;
    const extra = `\n\n🎁 Tengo el cupón ${coupon} (25% de descuento en mi primera valoración).`;
    return base + encodeURIComponent(extra);
  }, [quiz, result, coupon, couponActive]);

  const submitLead = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr("");
    try {
      await axios.post(`${API}/quiz/submit`, {
        quiz_id: quiz.id,
        quiz_title: quiz.title,
        score: result.score,
        result_color: result.color,
        result_label: result.label,
        recommended_service: quiz.therapist.service,
        recommended_therapist: quiz.therapist.name,
        answers: answers.map(Boolean),
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        coupon_code: couponActive ? coupon : "",
      });
      setSaved(true);
    } catch (e2) {
      setErr("No pudimos guardar tus datos. Inténtalo de nuevo.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="max-w-3xl mx-auto px-6 lg:px-8 py-14" data-testid="quiz-result">
      <div className="text-center">
        <div className="text-6xl mb-3" aria-hidden="true">{result.emoji}</div>
        <span className={`inline-flex rounded-full ${palette.bg} ${palette.text} border ${palette.border} px-4 py-1.5 text-xs font-bold tracking-widest uppercase`}>
          Tu resultado · {result.score}/{quiz.questions.length} puntos
        </span>
        <h1 className="mt-5 font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
          {result.label}
        </h1>
        <p className="mt-4 text-base text-slate-600 max-w-xl mx-auto leading-relaxed">
          {result.message}
        </p>
      </div>

      {/* Cupón con countdown */}
      {couponActive ? (
        <div className="mt-8 rounded-3xl bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500 p-1 shadow-xl shadow-orange-200/60" data-testid="quiz-coupon-banner">
          <div className="rounded-[22px] bg-white px-6 py-7 sm:px-8 sm:py-8">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-500 to-rose-500 text-white shadow-lg">
                  <Gift className="h-7 w-7" />
                </div>
                <div>
                  <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-orange-600">Oferta flash · solo para ti</p>
                  <p className="font-display text-2xl sm:text-3xl font-bold text-slate-900 leading-tight">
                    25% OFF en tu primera valoración
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-2.5 text-white">
                <Clock className="h-4 w-4 animate-pulse" />
                <span className="font-mono text-lg font-bold tracking-wider" data-testid="quiz-coupon-countdown">
                  {countdown.mm}:{countdown.ss}
                </span>
              </div>
            </div>
            <div className="mt-5 rounded-2xl bg-amber-50 border-2 border-dashed border-amber-300 px-5 py-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-amber-700">Tu código</p>
                <p className="font-mono text-2xl sm:text-3xl font-bold text-amber-900 tracking-wider" data-testid="quiz-coupon-code">
                  {coupon}
                </p>
              </div>
              <button
                type="button"
                onClick={() => { navigator.clipboard?.writeText(coupon); }}
                className="rounded-full bg-amber-900 text-white px-4 py-2 text-xs font-bold hover:bg-amber-800"
                data-testid="quiz-coupon-copy"
              >
                Copiar código
              </button>
            </div>
            <p className="mt-3 text-xs text-slate-500">
              Menciónalo al agendar (WhatsApp o calendario) para aplicar tu descuento. Válido solo en los próximos 3 minutos.
            </p>
          </div>
        </div>
      ) : (
        <div className="mt-8 rounded-3xl bg-slate-100 px-6 py-5 text-center" data-testid="quiz-coupon-expired">
          <p className="text-sm font-semibold text-slate-500">
            El cupón flash ha expirado. Aún así, agenda tu valoración — siempre tenemos algo especial para nuevos pacientes.
          </p>
        </div>
      )}

      {/* Terapeuta recomendado */}
      <div className="mt-8 rounded-3xl bg-white shadow-md border border-slate-100 overflow-hidden">
        <div className="px-6 py-4 bg-slate-900 text-white">
          <p className="text-[10px] font-bold tracking-widest uppercase text-green-400">Terapeuta recomendado/a para ti</p>
        </div>
        <div className="flex flex-col sm:flex-row items-center gap-5 p-6">
          <img
            src={quiz.therapist.photo}
            alt={quiz.therapist.name}
            className="h-24 w-24 sm:h-28 sm:w-28 rounded-2xl object-cover ring-2 ring-green-200"
            data-testid="quiz-therapist-photo"
          />
          <div className="flex-1 text-center sm:text-left">
            <p className="font-display text-xl font-bold text-slate-900">{quiz.therapist.name}</p>
            <p className="text-sm text-slate-500 mt-1">{quiz.therapist.role}</p>
            <div className="mt-4 flex flex-col sm:flex-row gap-2.5">
              <a
                href={waLink}
                target="_blank" rel="noopener noreferrer"
                className={`inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold text-white transition-colors ${palette.btn}`}
                data-testid="quiz-cta-whatsapp"
              >
                <Send className="h-4 w-4" /> Agendar por WhatsApp
              </a>
              <Link
                to={BOOKING_ANCHOR_BY_SERVICE[quiz.therapist.service] || "/#booking-fisio"}
                className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold text-slate-900 bg-white border-2 border-slate-200 hover:border-green-500"
                data-testid="quiz-cta-booking"
              >
                Ver calendario
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Captura de lead */}
      {!saved && (
        <form onSubmit={submitLead} className="mt-10 rounded-3xl bg-green-50/50 border border-green-100 p-6 sm:p-8" data-testid="quiz-lead-form">
          <p className="font-display text-lg font-bold text-slate-900">
            ¿Quieres recibir tu resultado por email + un beneficio exclusivo?
          </p>
          <p className="mt-1 text-sm text-slate-600">
            Te mandamos un resumen + un 10% de descuento en tu primera valoración. Sin spam, prometido.
          </p>
          <div className="mt-5 grid sm:grid-cols-2 gap-3">
            <input
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Tu nombre"
              className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm focus:border-green-500 focus:ring-2 focus:ring-green-100 outline-none"
              data-testid="quiz-lead-name"
            />
            <input
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Tu correo"
              className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm focus:border-green-500 focus:ring-2 focus:ring-green-100 outline-none"
              data-testid="quiz-lead-email"
            />
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Tu WhatsApp (opcional)"
              className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm focus:border-green-500 focus:ring-2 focus:ring-green-100 outline-none sm:col-span-2"
              data-testid="quiz-lead-phone"
            />
          </div>
          {err && <p className="mt-3 text-sm text-rose-600">{err}</p>}
          <div className="mt-4 flex items-center justify-between gap-4">
            <p className="text-xs text-slate-500 inline-flex items-center gap-1.5">
              <Lock className="h-3.5 w-3.5" /> Tus datos están seguros, no los compartimos con nadie.
            </p>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-full bg-green-600 hover:bg-green-700 px-6 py-3 text-sm font-bold text-white disabled:opacity-60"
              data-testid="quiz-lead-submit"
            >
              {saving ? "Enviando…" : "Recibir resultado"}
              {!saving && <Send className="h-4 w-4" />}
            </button>
          </div>
        </form>
      )}
      {saved && (
        <div className="mt-10 rounded-3xl bg-green-600 text-white p-6 text-center" data-testid="quiz-lead-saved">
          <Check className="h-10 w-10 mx-auto" />
          <p className="mt-3 font-display text-xl font-bold">¡Listo! Tu resultado está en camino.</p>
          <p className="mt-1 text-sm text-green-50/90">Revisa tu bandeja en los próximos minutos.</p>
        </div>
      )}

      <div className="mt-10 text-center">
        <button onClick={onRestart} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900" data-testid="quiz-restart">
          <RefreshCw className="h-4 w-4" /> Hacer otro quiz
        </button>
      </div>

      <div className="mt-10 rounded-3xl bg-gradient-to-br from-green-50 via-emerald-50 to-white border border-green-100 p-7 sm:p-9 text-center" data-testid="quiz-thanks">
        <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-green-600 text-white shadow-lg shadow-green-200">
          <Check className="h-7 w-7" />
        </div>
        <p className="mt-5 font-display text-xl sm:text-2xl font-bold text-slate-900 leading-tight max-w-xl mx-auto">
          Agradecemos tu visita
        </p>
        <p className="mt-2 text-base text-slate-600 leading-relaxed max-w-xl mx-auto italic">
          Será un placer conocerte y servirte profesionalmente en nuestro consultorio.
        </p>
        <p className="mt-4 text-xs uppercase tracking-[0.25em] font-semibold text-green-700">
          TerafiQuiro Wellness Center · Al cuidado de tu cuerpo
        </p>
      </div>
    </section>
  );
};

const Quiz = () => {
  const [step, setStep] = useState(STEPS.router);
  const [quizKey, setQuizKey] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);

  const quiz = quizKey ? QUIZZES[quizKey] : null;

  const onPickQuiz = (key) => {
    setQuizKey(key);
    setStep(STEPS.quiz);
  };

  const onQuizComplete = (ans) => {
    const r = evaluateQuiz(QUIZZES[quizKey], ans);
    setAnswers(ans);
    setResult(r);
    setStep(STEPS.result);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onRestart = () => {
    setStep(STEPS.router);
    setQuizKey(null);
    setResult(null);
    setAnswers([]);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <main className="min-h-screen bg-gradient-to-b from-white to-green-50/40 pt-24 pb-16" data-testid="quiz-page">
      {step === STEPS.router && <RouterScreen onPick={onPickQuiz} />}
      {step === STEPS.quiz && quiz && (
        <>
          <div className="max-w-2xl mx-auto px-6 lg:px-8">
            <p className="text-xs font-bold tracking-widest uppercase text-green-700 text-center">{quiz.badge}</p>
            <h1 className="mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 text-center">{quiz.title}</h1>
            <p className="mt-3 text-base text-slate-600 text-center max-w-xl mx-auto">{quiz.hook}</p>
          </div>
          <QuizScreen quiz={quiz} onComplete={onQuizComplete} onBack={onRestart} />
        </>
      )}
      {step === STEPS.result && quiz && result && (
        <ResultScreen quiz={quiz} result={result} answers={answers} onRestart={onRestart} />
      )}
    </main>
  );
};

export default Quiz;
